import React, { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { createSelector, nanoid } from '@reduxjs/toolkit'
import { BaseQueryFn, TypedUseQueryStateResult } from '@reduxjs/toolkit/query/react'
import { isEqual } from 'lodash'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'

import { doesSupportSmartSchedule } from '../common/domain/charging-schedules'
import getCurrentTimeZone from '../lib/getCurrentTimeZone'
import canEditChargePoint from '../lib/can-edit-charge-point'
import { getConnectorSelectedCurrentLimit } from '../lib/get-connector-selected-current-limit'
import isAura from '../lib/isAura'
import { isUserappDomain } from '../lib/currentDomain'
import useText from '../common/hooks/useText'
import {
    useSmartChargingScheduleGetByChargePointQuery,
    useSmartChargingScheduleGetChargePointScheduleStatusQuery,
    useChargePointGetQuery,
    Connector,
    ChargePoint,
    useSmartChargingScheduleClearMutation,
    useSmartChargingScheduleUpdateMutation,
    SmartChargingSchedule,
} from '../common/redux/api/web-api'
import { Scheduler } from '../common/uikit/Scheduler'
import { ScheduleEntry } from '../common/uikit/Scheduler/types'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../common/constants/ChargePointConstants'
import { useToast } from '../common/uikit/ToastProvider'
import { isFetchBaseQueryError } from '../common/redux/api/fetchBaseQueryWithReauth'
import { confirm } from '../common/uikit/ConfirmDialog'
import { useSessionUser } from '../common/hooks/useSessionUser'
import { InformationDialog } from '../common/dialogs/information-dialog'
import { ScheduledTimeSlotEditorDialog } from '../common/dialogs/scheduled-time-slot-editor-dialog'

import { ConnectorType } from './ConnectorSelect'
import ChargePointSchedulesControls from './ChargePointSchedulesControls'
import { MIN_SAFE_CURRENT, SCHUKO_CURRENT } from './CurrentSelect'

const defaultTimeZone = getCurrentTimeZone()

export interface ChargePointSchedulesPageProps {}

const hasAccessToConnector = (connector: Connector, userId?: string) => {
    if (connector === null || connector === undefined) return false
    return (
        userId === undefined ||
        userId === null || //Admin
        connector.connectorUserId === userId
    )
}

const hasAccess = (chargePoint: ChargePoint, userId?: string): number[] => {
    const numberOfConnectorsWithAccess: number[] = []
    for (const c of chargePoint?.connectors ?? []) {
        if (hasAccessToConnector(c, userId) && c.connectorId) {
            numberOfConnectorsWithAccess.push(c.connectorId)
        }
    }
    return numberOfConnectorsWithAccess
}

type GetSmartSchedulesWithPeriodIdsResult = TypedUseQueryStateResult<SmartChargingSchedule[], unknown, BaseQueryFn>

const selectSchedulesWithPeriodIds = createSelector(
    (result: GetSmartSchedulesWithPeriodIdsResult) => result.data,
    () => undefined,
    (data: SmartChargingSchedule[] | undefined) =>
        data?.map((schedule) => ({
            ...schedule,
            schedulePeriods: schedule.schedulePeriods?.map((period) => ({
                id: nanoid(),
                ...period,
            })),
        }))
)

export const ChargePointSchedulesPage = memo(function (): React.ReactElement | null {
    const text = useText()
    const { chargePointId } = useParams()
    const toast = useToast()
    const scheduleActionsMenuRef = useRef<Menu>(null)
    const user = useSessionUser()
    const hasAuthenticatedSession = Boolean(user.id)

    const { data: chargePoint, refetch: refetchChargePoint } = useChargePointGetQuery(
        { chargePointId: chargePointId ?? '', expand: 'ocppConfig' },
        { pollingInterval: CHARGE_POINT_REFRESH_TIMEOUT }
    )
    const { data: chargePointSchedules, isLoading: isSchedulesLoading } = useSmartChargingScheduleGetByChargePointQuery(
        {
            chargePointId: chargePointId ?? '',
        },
        {
            selectFromResult: (result) => ({
                ...result,
                data: selectSchedulesWithPeriodIds(result),
            }),
        }
    )

    const { data: chargePointScheduleStatus } = useSmartChargingScheduleGetChargePointScheduleStatusQuery(
        {
            chargePointId: chargePointId ?? '',
        },
        { pollingInterval: 5000 }
    )

    const [deleteSchedule, { isLoading: deleteScheduleIsLoading }] = useSmartChargingScheduleClearMutation()
    const [updateSchedule, { isLoading: updateScheduleIsLoading }] = useSmartChargingScheduleUpdateMutation()

    const isScheduleSynced = chargePointScheduleStatus === 'Synced'
    const [showStatusIcon, setShowStatusIcon] = useState(false)
    const [selectedConnectorId, selectConnectorId] = useState(1)

    const [isTimeSlotEditorDialogVisible, setIsTimeSlotEditorDialogVisible] = useState(false)

    const [selectedSchedulePeriod, setSelectedSchedulePeriod] = useState<ScheduleEntry | null>(null)

    const isChargerOffline = chargePoint?.chargePointStatus === 'Offline'

    const checkIsChargerEditable = useCallback(() => {
        if (chargePoint?.chargePointStatus === 'Online') return Promise.resolve()
        return refetchChargePoint().then(({ data }) => {
            if (data?.chargePointStatus === 'Online') return
            return Promise.reject()
        })
    }, [chargePoint?.chargePointStatus, refetchChargePoint])

    const numberOfConnectors = useMemo(() => {
        return chargePoint?.numberOfConnectors || chargePoint?.connectors?.length || 0
    }, [chargePoint?.connectors?.length, chargePoint?.numberOfConnectors])

    useEffect(() => {
        if (isUserappDomain && chargePoint && chargePoint?.ownerId === null) {
            const numberOfConnectorsWithAccess = hasAccess(chargePoint, user.id || undefined)
            selectConnectorId(numberOfConnectorsWithAccess[0] ?? 1)
        }
    }, [chargePoint, user.id])

    const [minPossibleCurrent, maxPossibleCurrent] = useMemo(() => {
        const selectedConnector = chargePoint?.connectors?.find((c) => c.connectorId === selectedConnectorId)
        if (selectedConnector?.connectorFunctionType === 'Schuko') return [SCHUKO_CURRENT, SCHUKO_CURRENT]
        const connectorsLimit = getConnectorSelectedCurrentLimit(chargePoint?.ocppConfigKeys ?? [], selectedConnectorId)
        return [MIN_SAFE_CURRENT, connectorsLimit]
    }, [selectedConnectorId, chargePoint])

    const getDefaultSchedule = useCallback(
        (connectorId = selectedConnectorId) => ({
            isActive: false,
            scheduleId: undefined,
            defaultCurrent: 0,
            schedulePeriods: [],
            timeZone: defaultTimeZone,
            connectorId,
            chargePointId,
        }),
        [chargePointId, selectedConnectorId]
    )

    const [selectedSchedule, setSelectedSchedule] = useState(
        chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId) || getDefaultSchedule()
    )

    const onSchedulesChange = useCallback(
        (schedulePeriods: ScheduleEntry[]) =>
            setSelectedSchedule((selectedSchedule) => ({ ...selectedSchedule, schedulePeriods, isActive: true })),
        []
    )

    const scheduleHasChanges = useMemo(() => {
        const existingSchedule = chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId)
        if (existingSchedule) {
            return !isEqual(existingSchedule, selectedSchedule)
        }

        return selectedSchedule?.schedulePeriods?.length
    }, [selectedSchedule, chargePointSchedules, selectedConnectorId])

    useEffect(() => {
        setSelectedSchedule(() => {
            return (
                chargePointSchedules?.find((schedule) => schedule.connectorId === selectedConnectorId) ||
                getDefaultSchedule()
            )
        })
    }, [chargePointSchedules, getDefaultSchedule, selectedConnectorId])

    const changeConnector = async (connectorId: number) => {
        if (selectedConnectorId === connectorId) return
        if (connectorId > numberOfConnectors) return

        if (scheduleHasChanges) {
            const confirmed = await confirm({
                header: text('lblProceedConfirmation'),
                message: text('msgChangeConnectorConfirm'),
                variant: 'danger',
            })
            if (!confirmed) return
        }

        setShowStatusIcon(false)
        setSelectedSchedule(
            chargePointSchedules?.find((schedule) => schedule.connectorId === connectorId) ||
                getDefaultSchedule(connectorId)
        )
        selectConnectorId(connectorId)
    }

    useEffect(() => {
        const handler = (ev: BeforeUnloadEvent) => {
            if (!scheduleHasChanges || !hasAuthenticatedSession) return
            ev.preventDefault()
            return (ev.returnValue = text('msgUnsavedChangesConfirm'))
        }
        window.addEventListener('beforeunload', handler, { capture: true })
        return () => window.removeEventListener('beforeunload', handler, { capture: true })
    }, [hasAuthenticatedSession, scheduleHasChanges, text])

    function hideSyncedIcon() {
        setTimeout(function () {
            setShowStatusIcon(false)
        }, 5000)
    }

    const saveSchedule = useCallback(() => {
        toast.notifyInfo(text('msgSavingSchedule'))

        return updateSchedule({ smartChargingSchedule: selectedSchedule })
            .unwrap()
            .then(() => {
                refetchChargePoint().then(() => {
                    setShowStatusIcon(true)
                    if (selectedConnectorId === ConnectorType.CHARGER || isAura(chargePoint)) {
                        toast.notifySuccess(text('msgSchedulesSaved'))
                    } else {
                        toast.notifySuccess(
                            text('msgSocketScheduleSaved', {
                                isCurrentLimitLowerThanDefaultCurrent:
                                    (selectedSchedule?.defaultCurrent || minPossibleCurrent) > maxPossibleCurrent,
                                connectorSelectedCurrentLimit: maxPossibleCurrent,
                            })
                        )
                    }
                })
            })
            .catch((error: unknown) => {
                if (isFetchBaseQueryError(error)) {
                    if (error.status === 410) {
                        return toast.notifyError(text('msgOfflineChargerScheduleIsNotEditable'))
                    }
                } else {
                    toast.notifyError(text('msgFailedToUpdateSchedule'))
                }
            })
    }, [
        chargePoint,
        maxPossibleCurrent,
        minPossibleCurrent,
        refetchChargePoint,
        selectedConnectorId,
        selectedSchedule,
        text,
        toast,
        updateSchedule,
    ])

    const handleClearSchedule = useCallback(async () => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgClearScheduleConfirm'),
            variant: 'danger',
        })
        if (!confirmed) return

        toast.notifyInfo(text('msgClearingSchedule'))

        setShowStatusIcon(true)
        return checkIsChargerEditable()
            .then(() => {
                deleteSchedule({ chargePointId: chargePointId ?? '', connectorId: selectedConnectorId }).unwrap()
                setSelectedSchedule(getDefaultSchedule())
            })
            .finally(() => {
                hideSyncedIcon()
            })
    }, [chargePointId, checkIsChargerEditable, deleteSchedule, getDefaultSchedule, selectedConnectorId, text, toast])

    const [isCurrentConflictInformationDialogVisible, setIsCurrentConflictInformationDialogVisible] = useState(false)
    const saveIfConfirmed = useCallback(() => {
        if (!hasAuthenticatedSession) {
            history.back() // will redirect user to the login page
            return
        }

        if (!selectedSchedule.schedulePeriods?.length) {
            return handleClearSchedule()
        }
        const hasCurrentOverlap = selectedSchedule.schedulePeriods.some(
            (period) => period.maxCurrent === selectedSchedule.defaultCurrent
        )
        if (hasCurrentOverlap) setIsCurrentConflictInformationDialogVisible(true)
        else saveSchedule()
    }, [
        hasAuthenticatedSession,
        selectedSchedule.schedulePeriods,
        selectedSchedule.defaultCurrent,
        saveSchedule,
        handleClearSchedule,
    ])

    const scheduleDisabled =
        isSchedulesLoading ||
        updateScheduleIsLoading ||
        deleteScheduleIsLoading ||
        isChargerOffline ||
        !chargePoint ||
        !doesSupportSmartSchedule(chargePoint) ||
        !canEditChargePoint(chargePoint, user)

    const scheduleDisabledReason = (() => {
        if (isSchedulesLoading || !chargePoint) return text('msgScheduleLoading')
        if (updateScheduleIsLoading) return text('msgSavingSchedule')
        if (deleteScheduleIsLoading) return text('msgClearingSchedule')
        if (isChargerOffline) return text('msgOfflineChargerScheduleIsNotEditable')
        if (!doesSupportSmartSchedule(chargePoint)) return text('msgSmartSchedulingNotSupported')
        if (!canEditChargePoint(chargePoint, user)) return text('msgNoPermissionToEditCharger')
        return undefined
    })()

    const disabledClear =
        !selectedSchedule.schedulePeriods ||
        scheduleDisabled ||
        isChargerOffline ||
        !(selectedSchedule?.schedulePeriods?.length > 0 || scheduleHasChanges)
    const disabledClearReason = disabledClear ? scheduleDisabledReason ?? text('msgNoSchedulesHeader') : undefined
    const disabledSaveReason = scheduleDisabled
        ? scheduleDisabledReason
        : !scheduleHasChanges
          ? text('msgNoChangesToSave')
          : !isScheduleSynced
            ? text('msgScheduleChangesStillSyncing')
            : undefined

    const onEditSchedulePeriod = () => {
        if (selectedSchedulePeriod) {
            setIsTimeSlotEditorDialogVisible(true)
        }
    }

    const onDeleteSchedulePeriod = () => {
        if (selectedSchedulePeriod && selectedSchedule) {
            const filtered = selectedSchedule.schedulePeriods?.filter(
                (nextPeriod) =>
                    !(nextPeriod.from === selectedSchedulePeriod.from && nextPeriod.to === selectedSchedulePeriod.to)
            )
            setSelectedSchedule({
                ...selectedSchedule,
                schedulePeriods: filtered,
            })
        }
    }

    const scheduleActions: MenuItem[] = [
        {
            label: text('btnEdit'),
            icon: 'pi pi-pencil',
            command: () => {
                onEditSchedulePeriod()
            },
        },
        {
            label: text('lblDelete'),
            icon: 'pi pi-trash',
            command: () => {
                onDeleteSchedulePeriod()
            },
        },
    ]

    const getWarningMessage = useCallback(
        (schedulePeriod: ScheduleEntry) => {
            if (schedulePeriod.maxCurrent === selectedSchedule.defaultCurrent) {
                return text('msgTimeRangeEqualsDefaultCurrent')
            }
            if (maxPossibleCurrent < schedulePeriod.maxCurrent) {
                if (selectedConnectorId === ConnectorType.CHARGER || isAura(chargePoint)) {
                    return text('msgChargingUpTo', { connectorSelectedCurrentLimit: maxPossibleCurrent })
                }
                return text('msgSocketMightNotBeChargingOptimally', {
                    connectorSelectedCurrentLimit: maxPossibleCurrent,
                })
            }
            return null
        },
        [chargePoint, maxPossibleCurrent, selectedConnectorId, selectedSchedule.defaultCurrent, text]
    )

    const getCustomStyle = useCallback(
        (schedulePeriod: ScheduleEntry) => {
            if (schedulePeriod.maxCurrent === selectedSchedule.defaultCurrent) {
                return { backgroundColor: 'var(--lightCoral)' }
            }
            return {}
        },
        [selectedSchedule.defaultCurrent]
    )

    const getCustomMessage = useCallback(() => {
        if (isAura(chargePoint)) {
            return selectedConnectorId === 1 ? text('lblLeftSocket') : text('lblRightSocket')
        }
        return selectedConnectorId === ConnectorType.CHARGER ? text('lblChargerOnly') : text('lblSocketOnly')
    }, [chargePoint, selectedConnectorId, text])

    const isSocketConnector = !isAura(chargePoint) && selectedConnectorId === ConnectorType.SOCKET

    const formatCurrent = useCallback(
        (maxCurrent: number) => {
            if (isSocketConnector) {
                return maxCurrent ? text('lblOn') : text('lblOff')
            }

            return maxCurrent ? text('msgAmpereValue', { current: maxCurrent }) : text('lblDisabledStatus')
        },
        [isSocketConnector, text]
    )

    const openScheduleActions = useCallback((event: React.MouseEvent, schedulePeriod: ScheduleEntry) => {
        setSelectedSchedulePeriod(schedulePeriod)
        scheduleActionsMenuRef.current?.toggle(event)
    }, [])

    return (
        <>
            <InformationDialog
                visible={isCurrentConflictInformationDialogVisible}
                title={text('lblScheduleCurrentConflictDialogTitle')}
                message={text('msgChangeDefaultCurrent')}
                onHide={() => setIsCurrentConflictInformationDialogVisible(false)}
            />

            <ChargePointSchedulesControls
                schedule={selectedSchedule}
                onUpdateSchedule={setSelectedSchedule}
                onSaveSchedule={saveIfConfirmed}
                onClearSchedule={handleClearSchedule}
                chargePoint={chargePoint}
                numberOfConnectors={numberOfConnectors}
                connectorId={selectedConnectorId}
                onChangeConnectorId={changeConnector}
                disabledDefaultCurrent={!selectedSchedule?.schedulePeriods?.length}
                minPossibleCurrent={minPossibleCurrent}
                maxPossibleCurrent={maxPossibleCurrent}
                disabledSave={disabledSaveReason ?? false}
                syncStatus={chargePointScheduleStatus}
                disabledControls={scheduleDisabled}
                disabledClear={disabledClear}
                disabledClearReason={disabledClearReason}
                showSyncedStatusIcon={showStatusIcon}
            />

            <Scheduler
                maxCurrent={maxPossibleCurrent}
                isActive={selectedSchedule?.isActive}
                schedules={selectedSchedule?.schedulePeriods ?? []}
                onSchedulesChange={onSchedulesChange}
                disabled={scheduleDisabled}
                getWarningMessage={getWarningMessage}
                getCustomStyle={getCustomStyle}
                getCustomMessage={getCustomMessage}
                formatCurrent={formatCurrent}
                actionsLabel={text('lblScheduledTimeSlotActions')}
                onOpenActions={openScheduleActions}
            />

            <ScheduledTimeSlotEditorDialog
                visible={isTimeSlotEditorDialogVisible}
                onHide={() => setIsTimeSlotEditorDialogVisible(false)}
                selectedSchedulePeriod={selectedSchedulePeriod}
                minPossibleCurrent={minPossibleCurrent}
                maxPossibleCurrent={maxPossibleCurrent}
                onSave={(schedulePeriod) => {
                    setIsTimeSlotEditorDialogVisible(false)
                    if (selectedSchedule) {
                        setSelectedSchedule({
                            ...selectedSchedule,
                            schedulePeriods: selectedSchedule.schedulePeriods?.map((period) =>
                                period.id === schedulePeriod.id ? schedulePeriod : period
                            ),
                        })
                    }
                }}
            />

            <Menu id="scheduled-time-slot-actions-menu" model={scheduleActions} ref={scheduleActionsMenuRef} popup />
        </>
    )
})
